import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Plus, Save, Loader2, AlertCircle, Library, Eye } from 'lucide-react'
import { DndContext, closestCorners, type DragEndEvent } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { SectionBlock } from '../components/FormBuilder/SectionBlock'
import { FieldConfigPanel } from '../components/FormBuilder/FieldConfigPanel'
import { LibrarySectionPicker } from '../components/FormBuilder/LibrarySectionPicker'
import { FormPreviewDialog } from '../components/FormBuilder/FormPreviewDialog'
import { useAdminFormBuilder } from '../hooks/useAdminFormBuilder'
import type { ApplicationField } from '@/types'

export function AdminFormBuilderPage() {
  const { id: applicationId } = useParams<{ id: string }>()
  const [editingField, setEditingField] = useState<ApplicationField | null>(null)
  const [configOpen, setConfigOpen] = useState(false)
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null)
  const [sectionLibraryOpen, setSectionLibraryOpen] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)

  const {
    sections,
    loading,
    saving,
    error,
    isDirty,
    addSection,
    addSectionFromLibrary,
    updateSection,
    deleteSection,
    reorderSections,
    addField,
    addFieldFromLibrary,
    updateField,
    deleteField,
    reorderFields,
    moveField,
    save,
  } = useAdminFormBuilder(applicationId ?? '')

  const sortedSections = [...sections].sort((a, b) => a.order - b.order)
  const sectionIds = sortedSections.map((s) => s.id)

  function findSectionContainingField(fieldId: string): { sectionId: string; index: number } | null {
    for (const s of sortedSections) {
      const sorted = [...s.fields].sort((a, b) => a.order - b.order)
      const idx = sorted.findIndex((f) => f.id === fieldId)
      if (idx !== -1) return { sectionId: s.id, index: idx }
    }
    return null
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const activeId = String(active.id)
    const overId = String(over.id)

    // Section reorder
    if (sectionIds.includes(activeId)) {
      if (!sectionIds.includes(overId)) return
      const oldIndex = sectionIds.indexOf(activeId)
      const newIndex = sectionIds.indexOf(overId)
      const reordered = arrayMove(sortedSections, oldIndex, newIndex)
      reorderSections(reordered.map((s) => s.id))
      return
    }

    // Field drag
    const source = findSectionContainingField(activeId)
    if (!source) return

    // Dropped onto a section's empty/append zone
    if (overId.startsWith('section-dropzone:')) {
      const toSectionId = overId.slice('section-dropzone:'.length)
      const toSection = sortedSections.find((s) => s.id === toSectionId)
      const toIndex = toSection ? toSection.fields.length : 0
      moveField(source.sectionId, activeId, toSectionId, toIndex)
      return
    }

    // Dropped onto another field
    const target = findSectionContainingField(overId)
    if (!target) return

    if (source.sectionId === target.sectionId) {
      const section = sortedSections.find((s) => s.id === source.sectionId)!
      const sorted = [...section.fields].sort((a, b) => a.order - b.order)
      const reordered = arrayMove(sorted, source.index, target.index)
      reorderFields(source.sectionId, reordered.map((f) => f.id))
    } else {
      moveField(source.sectionId, activeId, target.sectionId, target.index)
    }
  }

  function openFieldEdit(sectionId: string, field: ApplicationField) {
    setEditingSectionId(sectionId)
    setEditingField(field)
    setConfigOpen(true)
  }

  function handleFieldUpdate(patch: Partial<ApplicationField>) {
    if (!editingField || !editingSectionId) return
    const updated = { ...editingField, ...patch }
    setEditingField(updated)
    updateField(editingSectionId, editingField.id, patch)
  }

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button asChild variant="ghost" size="sm">
            <Link to="/admin/applications">← Back to Applications</Link>
          </Button>
          <h1 className="text-2xl font-bold">Form Builder</h1>
          {isDirty && (
            <Badge variant="secondary" className="text-xs">
              Unsaved changes
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
        <Button type="button" variant="outline" onClick={() => setPreviewOpen(true)} disabled={sortedSections.length === 0}>
          <Eye className="mr-2 h-4 w-4" />
          Preview
        </Button>
        <Button onClick={() => void save()} disabled={saving || !isDirty}>
          {saving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving…
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              Save Form
            </>
          )}
        </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Sections */}
      <DndContext collisionDetection={closestCorners} onDragEnd={handleDragEnd}>
        <SortableContext items={sectionIds} strategy={verticalListSortingStrategy}>
          <div className="space-y-4">
            {sortedSections.map((section) => (
              <SectionBlock
                key={section.id}
                section={section}
                onUpdate={(patch) => updateSection(section.id, patch)}
                onDelete={() => deleteSection(section.id)}
                onAddField={(type) => addField(section.id, type)}
                onAddFieldFromLibrary={(libId) => addFieldFromLibrary(section.id, libId)}
                onEditField={(field) => openFieldEdit(section.id, field)}
                onDeleteField={(fieldId) => deleteField(section.id, fieldId)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {sortedSections.length === 0 && (
        <div className="rounded-lg border-2 border-dashed border-muted-foreground/20 py-16 text-center">
          <p className="text-muted-foreground">No sections yet.</p>
          <p className="text-sm text-muted-foreground">Add a section to start building your form.</p>
        </div>
      )}

      <div className="flex gap-2">
        <Button type="button" variant="outline" onClick={addSection}>
          <Plus className="mr-2 h-4 w-4" />
          Add Section
        </Button>
        <Button type="button" variant="outline" onClick={() => setSectionLibraryOpen(true)}>
          <Library className="mr-2 h-4 w-4" />
          Add from Library
        </Button>
      </div>

      {/* Field config panel */}
      <FieldConfigPanel
        field={editingField}
        open={configOpen}
        onOpenChange={setConfigOpen}
        onUpdate={handleFieldUpdate}
      />

      <FormPreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        sections={sortedSections}
      />

      <LibrarySectionPicker
        open={sectionLibraryOpen}
        onOpenChange={setSectionLibraryOpen}
        onSelect={addSectionFromLibrary}
      />
    </div>
  )
}
